/**
 * Loading skeleton for the v2 Edit screen body — mirrors the real
 * grid's structure (outer Type pill + NDC tabs + chip row + main grid
 * with TOTAL row + SU Prices block) so the page doesn't visually
 * lurch when data lands.
 *
 * Why a hand-rolled skeleton rather than a few PrimeReact <Skeleton>
 * bars: the actual grid is a tightly-laid table with sticky columns
 * and a CMOP/TOTAL partition, and operators benefit from seeing the
 * SAME shape during load. Generic gray bars under a fully-formed
 * header create a noticeable "click here is going to move" feeling.
 *
 * All shimmer surfaces use PrimeReact's `<Skeleton>` so the animation
 * matches the rest of the app's loading idiom.
 */

import { Skeleton } from 'primereact/skeleton';

import styles from './forecastGridSkeleton.module.scss';

// Visible-column counts mirror the production grid — 12 month
// columns fit comfortably in the typical viewport, the rest scroll.
const SKEL_MONTH_COLS = 12;
const SKEL_BODY_ROWS = 4;
const SU_BODY_ROWS = 4;

const ForecastGridSkeleton = () => {
  return (
    <div className={styles.wrap}>
      {/* Outer Type tab pill — mimics the NDCHeader-styled "MAS" tab */}
      <div className={styles.outerTabStrip}>
        <Skeleton width="72px" height="32px" borderRadius="6px" />
      </div>

      {/* Inner NDC tab strip */}
      <div className={styles.innerTabStrip}>
        <Skeleton width="140px" height="34px" borderRadius="6px" />
        <Skeleton width="140px" height="34px" borderRadius="6px" />
      </div>

      {/* Pack-size chip row */}
      <div className={styles.chipRow}>
        <Skeleton width="60px" height="22px" borderRadius="4px" />
        <Skeleton width="60px" height="22px" borderRadius="4px" />
        <Skeleton width="60px" height="22px" borderRadius="4px" />
        <Skeleton width="60px" height="22px" borderRadius="4px" />
      </div>

      {/* Section heading */}
      <div className={styles.sectionHeading}>
        <Skeleton width="220px" height="14px" />
      </div>

      {/* Main forecast grid — header + body + TOTAL */}
      <div className={styles.tableWrap}>
        <table className={styles.table}>
          <thead>
            <tr>
              <th className={`${styles.headCell} ${styles.stickyFacility}`}>
                <Skeleton width="80%" height="14px" />
              </th>
              <th className={`${styles.headCell} ${styles.stickyAnnual}`}>
                <Skeleton width="80%" height="14px" />
              </th>
              <th className={`${styles.headCell} ${styles.stickyPct}`}>
                <Skeleton width="60%" height="14px" />
              </th>
              {Array.from({ length: SKEL_MONTH_COLS }).map((_, i) => (
                <th key={i} className={styles.headCell}>
                  <Skeleton width="60%" height="14px" />
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {Array.from({ length: SKEL_BODY_ROWS }).map((_, rIdx) => (
              <tr key={rIdx}>
                <td className={`${styles.bodyCell} ${styles.stickyFacility}`}>
                  <Skeleton width="70%" height="14px" />
                </td>
                <td className={`${styles.bodyCell} ${styles.stickyAnnual}`}>
                  <Skeleton width="55%" height="14px" className={styles.alignRight} />
                </td>
                <td className={`${styles.bodyCell} ${styles.stickyPct}`}>
                  <Skeleton width="50%" height="14px" className={styles.alignRight} />
                </td>
                {Array.from({ length: SKEL_MONTH_COLS }).map((_, mIdx) => (
                  <td key={mIdx} className={styles.bodyCell}>
                    <div className={styles.cellStack}>
                      <Skeleton width="46px" height="22px" borderRadius="4px" />
                      <Skeleton width="36px" height="10px" />
                    </div>
                  </td>
                ))}
              </tr>
            ))}
            {/* TOTAL row — same gradient background the real grid uses */}
            <tr className={styles.totalRow}>
              <td className={`${styles.bodyCell} ${styles.stickyFacility} ${styles.totalCell}`}>
                <Skeleton width="50%" height="14px" />
              </td>
              <td className={`${styles.bodyCell} ${styles.stickyAnnual} ${styles.totalCell}`}>
                <Skeleton width="55%" height="14px" />
              </td>
              <td className={`${styles.bodyCell} ${styles.stickyPct} ${styles.totalCell}`}>
                <Skeleton width="50%" height="14px" />
              </td>
              {Array.from({ length: SKEL_MONTH_COLS }).map((_, mIdx) => (
                <td key={mIdx} className={`${styles.bodyCell} ${styles.totalCell}`}>
                  <Skeleton width="55%" height="14px" />
                </td>
              ))}
            </tr>
          </tbody>
        </table>
      </div>

      {/* SU Prices block heading */}
      <div className={styles.suHeading}>
        <Skeleton width="120px" height="14px" />
        <Skeleton width="280px" height="11px" />
      </div>

      {/* SU Prices grid — simpler layout, single sticky column */}
      <div className={styles.tableWrap}>
        <table className={styles.table}>
          <thead>
            <tr>
              <th className={`${styles.headCell} ${styles.stickyFacility}`}>
                <Skeleton width="80%" height="14px" />
              </th>
              {Array.from({ length: SKEL_MONTH_COLS }).map((_, i) => (
                <th key={i} className={styles.headCell}>
                  <Skeleton width="60%" height="14px" />
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {Array.from({ length: SU_BODY_ROWS }).map((_, rIdx) => (
              <tr key={rIdx}>
                <td className={`${styles.bodyCell} ${styles.stickyFacility}`}>
                  <Skeleton width="70%" height="14px" />
                </td>
                {Array.from({ length: SKEL_MONTH_COLS }).map((_, mIdx) => (
                  <td key={mIdx} className={styles.bodyCell}>
                    <Skeleton width="50%" height="14px" />
                  </td>
                ))}
              </tr>
            ))}
            <tr className={styles.totalRow}>
              <td className={`${styles.bodyCell} ${styles.stickyFacility} ${styles.totalCell}`}>
                <Skeleton width="50%" height="14px" />
              </td>
              {Array.from({ length: SKEL_MONTH_COLS }).map((_, mIdx) => (
                <td key={mIdx} className={`${styles.bodyCell} ${styles.totalCell}`}>
                  <Skeleton width="55%" height="14px" />
                </td>
              ))}
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default ForecastGridSkeleton;
